import '@/styles/globals.css';
import 'mapbox-gl/dist/mapbox-gl.css';
import 'animate.css';
import 'aos/dist/aos.css';
import Head from 'next/head';
import { useMemo } from 'react';
import { JetBrains_Mono, Manrope } from 'next/font/google';
import { CssBaseline } from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import Layout from '@/components/layout/Layout';
import { AuthProvider } from '@/contexts/AuthContext';
import AuthGuard from '@/components/auth/AuthGuard';
import NotificationProvider from '@/contexts/NotificationContext';
import { ThemeModeProvider, useThemeMode } from '@/contexts/ThemeModeContext';

const manrope = Manrope({
    subsets: ['latin'],
    variable: '--font-manrope',
    display: 'swap',
});

const jetBrainsMono = JetBrains_Mono({
    subsets: ['latin'],
    variable: '--font-jetbrains-mono',
    display: 'swap',
});

const APP_FONT_STACK = `var(--font-manrope), ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"`;

function ModeAwareThemeProvider({ children }) {
    const { resolvedMode } = useThemeMode();

    const muiTheme = useMemo(() => createTheme({
        palette: {
            mode: resolvedMode,
            primary: {
                main: '#14b8a6',
            },
            background: {
                default: 'var(--app-bg)',
                paper: 'var(--surface)',
            },
            text: {
                primary: 'var(--text)',
                secondary: 'var(--text-muted)',
            },
            divider: 'var(--border)',
        },
        typography: {
            fontFamily: APP_FONT_STACK,
        },
        components: {
            MuiCssBaseline: {
                styleOverrides: {
                    body: {
                        backgroundColor: 'var(--app-bg)',
                        color: 'var(--text)',
                    },
                },
            },
            MuiSlider: {
                styleOverrides: {
                    thumb: {
                        backgroundColor: 'var(--accent)',
                    },
                    track: {
                        backgroundColor: 'var(--accent)',
                        border: 'none',
                    },
                    rail: {
                        backgroundColor: 'var(--surface-muted)',
                        opacity: 1,
                    },
                    valueLabel: {
                        backgroundColor: 'var(--chart-tooltip-bg)',
                        color: 'var(--text)',
                        border: '1px solid var(--chart-tooltip-border)',
                    },
                },
            },
        },
    }), [resolvedMode]);

    return (
        <ThemeProvider theme={muiTheme}>
            <CssBaseline enableColorScheme />
            {children}
        </ThemeProvider>
    );
}

function MyApp({ Component, pageProps }) {
    const useLayout = Component.useLayout !== false;

    const content = useLayout ? (
        <Layout>
            <Component {...pageProps} />
        </Layout>
    ) : (
        <Component {...pageProps} />
    );

    return (
        <div className={`${manrope.className} ${manrope.variable} ${jetBrainsMono.variable}`}>
            <ThemeModeProvider>
                <ModeAwareThemeProvider>
                    <AuthProvider>
                        <NotificationProvider>
                            <Head>
                                <meta name="description" content="ISABHEL delivers integrated satellite and ground-based bridge health monitoring." />
                                <meta name="viewport" content="width=device-width, initial-scale=1" />
                                <meta property="og:title" content="ISABHEL" />
                                <meta property="og:description" content="Advanced structural health assessment platform for bridges." />
                            </Head>
                            <AuthGuard>{content}</AuthGuard>
                        </NotificationProvider>
                    </AuthProvider>
                </ModeAwareThemeProvider>
            </ThemeModeProvider>
        </div>
    );
}

export default MyApp;
